import { AnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentContext';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { FORM_RESETS } from '@/domain/mitarbeiter/features/anlageassistent/context/formResets';
import { createContextConfig } from '@/domain/mitarbeiter/features/anlageassistent/context/utils';
import { useSubStepSchemasMap } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useFormSchema';
import { useGuttageStepSync } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useGuttageStepSync';
import {
  AnlageassistentContextType,
  FieldPath,
  Step,
  StepKey,
  SubStep,
  SubStepKey,
} from '@/domain/mitarbeiter/features/anlageassistent/types/types';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

// oxlint-disable-next-line max-lines-per-function
function AnlageassistentContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();

  const form = useAnlageassistentFormContext();
  const subStepSchemasMap = useSubStepSchemasMap();
  const showGuttageStep =
    currentStandort.isGuttagekontenAktiviert &&
    form.state.values.bezahlung.bezahlungsart.art === 'fixgehalt' &&
    form.state.values.bezahlung.arbeitszeitkonto.gehaltsempfaenger
      .sollstunden === true;

  const [anlageassistentState, setAnlageassistentState] = useState<
    AnlageassistentContextType['state']
  >(createContextConfig({ t, showGuttageStep }));

  useGuttageStepSync({
    showGuttageStep,
    anlageassistentState,
    setAnlageassistentState,
  });

  function getCurrentStep(): Step {
    return anlageassistentState.steps[anlageassistentState.currentStepIndex];
  }

  function applySubStepIssues(
    subStepKey: SubStepKey,
    issues: z.core.$ZodIssue[],
  ): void {
    issues.forEach((issue) => {
      const fieldPath = toFieldPath(subStepKey, issue);
      form.setFieldMeta(fieldPath, (prev) => ({
        ...prev,
        errorMap: {
          onSubmit: issue,
        },
      }));
    });
  }

  function areFieldsValid(fieldPaths: FieldPath[]): boolean {
    return fieldPaths.every(
      (fieldPath) => form.getFieldMeta(fieldPath)?.isValid === true,
    );
  }

  function toFieldPath(
    subStepKey: SubStepKey,
    issue: z.core.$ZodIssue,
  ): FieldPath {
    if (issue.path.length === 0) {
      return subStepKey as FieldPath;
    }

    return `${subStepKey}.${issue.path.join('.')}` as FieldPath;
  }

  function getSubStepFieldPaths(subStepKey: SubStepKey): FieldPath[] {
    const subStepPrefix = `${subStepKey}.`;
    return Object.keys(form.state.fieldMeta).filter(
      (key) => key === subStepKey || key.startsWith(subStepPrefix),
    ) as FieldPath[];
  }

  function validateStepReady(step: Step): boolean {
    if (step.type === 'summary') {
      return false;
    }

    return step.subSteps.every((subStep) => {
      const parseResult = subStepSchemasMap[subStep.key].safeParse(
        form.getFieldValue(subStep.key),
      );
      return parseResult.success;
    });
  }

  async function validateCurrentSubStep(): Promise<boolean> {
    const currentStep = getCurrentStep();
    if (currentStep.type === 'summary') {
      return true;
    }
    const currentSubStep =
      currentStep.subSteps[anlageassistentState.currentSubStepIndex];

    const parseResult = subStepSchemasMap[currentSubStep.key].safeParse(
      form.getFieldValue(currentSubStep.key),
    );
    const fieldPaths = getSubStepFieldPaths(currentSubStep.key);

    fieldPaths.forEach((fieldPath) => {
      form.setFieldMeta(fieldPath, (prev) => ({
        ...prev,
        errorMap: {},
      }));
    });

    if (!parseResult.success) {
      // field errorMap muss manuell gesetzt werden, da durch form validate die gesamte form validiert wird.
      applySubStepIssues(currentSubStep.key, parseResult.error.issues);
    }

    return parseResult.success && areFieldsValid(fieldPaths);
  }

  // eslint-disable-next-line complexity
  async function navigateToStep(step: StepKey | number) {
    const stepIndex =
      typeof step === 'number'
        ? step
        : anlageassistentState.steps.findIndex((s) => s.key === step);
    if (stepIndex === -1) {
      return;
    }

    if (stepIndex > anlageassistentState.currentStepIndex) {
      if ((await validateCurrentSubStep()) === false) {
        return;
      }
      for (let i = anlageassistentState.currentStepIndex; i < stepIndex; i++) {
        if (anlageassistentState.steps[i].type === 'summary') {
          continue;
        }
        if (validateStepReady(anlageassistentState.steps[i]) === false) {
          return;
        }
      }
    }

    setAnlageassistentState((prev) => ({
      ...prev,
      currentStepIndex: stepIndex,
      currentSubStepIndex: 0,
    }));
  }

  async function navigateToSubStep(subStepKey: SubStepKey) {
    anlageassistentState.steps.forEach((step) => {
      if (step.type === 'summary') {
        return;
      }
      step.subSteps.forEach(async (subStep) => {
        if (subStep.key === subStepKey) {
          await navigateToStep(step.key);
          setAnlageassistentState((prev) => ({
            ...prev,
            currentSubStepIndex: step.subSteps.findIndex(
              (s) => s.key === subStepKey,
            ),
          }));
          return;
        }
      });
    });
  }

  function hasNextStep(): boolean {
    return (
      anlageassistentState.currentStepIndex <
      anlageassistentState.steps.length - 1
    );
  }

  function hasNextSubStep(): boolean {
    const currentStep =
      anlageassistentState.steps[anlageassistentState.currentStepIndex];
    if (currentStep.type === 'summary') {
      return false;
    }
    return (
      anlageassistentState.currentSubStepIndex < currentStep.subSteps.length - 1
    );
  }

  function hasPreviousStep(): boolean {
    return anlageassistentState.currentStepIndex > 0;
  }

  function hasPreviousSubStep(): boolean {
    return anlageassistentState.currentSubStepIndex > 0;
  }

  async function next() {
    if (hasNextSubStep()) {
      if (await validateCurrentSubStep()) {
        setAnlageassistentState((prev) => ({
          ...prev,
          currentSubStepIndex: prev.currentSubStepIndex + 1,
        }));
      }
    } else if (hasNextStep()) {
      if (await validateCurrentSubStep()) {
        setAnlageassistentState((prev) => ({
          ...prev,
          currentStepIndex: prev.currentStepIndex + 1,
          currentSubStepIndex: 0,
        }));
      }
    }
  }

  function previous() {
    if (hasPreviousSubStep()) {
      setAnlageassistentState((prev) => ({
        ...prev,
        currentSubStepIndex: prev.currentSubStepIndex - 1,
      }));
    } else if (hasPreviousStep()) {
      setAnlageassistentState((prev) => {
        const previousStep = prev.steps[prev.currentStepIndex - 1];
        return {
          ...prev,
          currentStepIndex: prev.currentStepIndex - 1,
          currentSubStepIndex:
            previousStep.type === 'summary'
              ? 0
              : previousStep.subSteps.length > 0
                ? previousStep.subSteps.length - 1
                : 0,
        };
      });
    }
  }

  function currentStepProgress(): number | null {
    const currentStep =
      anlageassistentState.steps[anlageassistentState.currentStepIndex];
    if (currentStep.type === 'summary') {
      return null;
    }
    return (
      ((anlageassistentState.currentSubStepIndex + 1) /
        (currentStep.subSteps.length > 0 ? currentStep.subSteps.length : 1)) *
      100
    );
  }

  function getCurrentSubStep(): SubStep | null {
    const currentStep = getCurrentStep();
    if (currentStep.type === 'summary') {
      return null;
    }
    return currentStep.subSteps[anlageassistentState.currentSubStepIndex];
  }

  function resetDependentFields(field: FieldPath) {
    FORM_RESETS[field]?.forEach((fieldToReset) => {
      form.resetField(fieldToReset);
    });
  }

  return (
    <AnlageassistentContext.Provider
      value={{
        state: anlageassistentState,
        setState: setAnlageassistentState,
        navigateToStep,
        navigateToSubStep,
        validateStepReady,
        validateCurrentSubStep,
        next,
        previous,
        hasNext: hasNextStep,
        hasPrevious: () => hasPreviousStep() || hasPreviousSubStep(),
        currentStepProgress,
        getCurrentSubStep,
        resetDependentFields,
      }}>
      {children}
    </AnlageassistentContext.Provider>
  );
}

export { AnlageassistentContextProvider };
